import React from 'react';
import { parseServerDate } from '../../utils/time';

export const ORDER_STATUSES = [
  { value: 'draft', label: 'Черновик', cls: 'bg-gray-500/15 text-gray-500 border-gray-500/30 dark:text-gray-300' },
  { value: 'supplier_query', label: 'Новый — ждёт продавца', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  { value: 'supplier_confirmed', label: 'Принят продавцом', cls: 'bg-sky-500/15 text-sky-500 border-sky-500/30' },
  { value: 'buyer_notified', label: 'Покупатель уведомлён', cls: 'bg-sky-500/15 text-sky-500 border-sky-500/30' },
  { value: 'logistics_search', label: 'Поиск логиста', cls: 'bg-violet-500/15 text-violet-500 border-violet-500/30' },
  { value: 'logistics_assigned', label: 'Логист назначен', cls: 'bg-violet-500/15 text-violet-500 border-violet-500/30' },
  { value: 'order_formed', label: 'Сформирован', cls: 'bg-indigo-500/15 text-indigo-500 border-indigo-500/30' },
  { value: 'order_confirmed', label: 'Подтверждён', cls: 'bg-indigo-500/15 text-indigo-500 border-indigo-500/30' },
  { value: 'logistics_enroute', label: 'Отправлен / в пути', cls: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  { value: 'loading', label: 'Загрузка', cls: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  { value: 'loaded', label: 'Загружен', cls: 'bg-blue-500/15 text-blue-500 border-blue-500/30' },
  { value: 'delivered', label: 'Доставлен', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  { value: 'act_signed', label: 'Акт подписан', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  { value: 'paid', label: 'Завершён', cls: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' },
  { value: 'cancelled', label: 'Отменён', cls: 'bg-rose-500/15 text-rose-500 border-rose-500/30' },
];

/** Вкладки-группы над списком заказов (статусы бэкенда объединены по смыслу). */
export const STATUS_GROUPS = [
  { key: 'all', label: 'Все', statuses: [] },
  { key: 'new', label: 'Новые', statuses: ['draft', 'supplier_query'] },
  {
    key: 'in_work',
    label: 'В работе',
    statuses: ['supplier_confirmed', 'buyer_notified', 'logistics_search', 'logistics_assigned', 'order_formed', 'order_confirmed'],
  },
  { key: 'shipping', label: 'В пути', statuses: ['logistics_enroute', 'loading', 'loaded'] },
  { key: 'done', label: 'Доставлены', statuses: ['delivered', 'act_signed', 'paid'] },
  { key: 'cancelled', label: 'Отменённые', statuses: ['cancelled'] },
];

export const PAYMENT_STATUSES = [
  { value: 'unpaid', label: 'Не оплачен', cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  { value: 'paid', label: 'Оплачен', cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  { value: 'refunded', label: 'Возврат', cls: 'bg-sky-500/15 text-sky-500 border-sky-500/30' },
];

export const PAYMENT_METHODS = [
  { value: 'card', label: 'Карта' },
  { value: 'balance', label: 'Баланс' },
  { value: 'kaspi_direct', label: 'Kaspi напрямую' },
];

export const statusMeta = (value) =>
  ORDER_STATUSES.find((s) => s.value === value) || { value, label: value || '—', cls: ORDER_STATUSES[0].cls };
export const paymentMeta = (value) =>
  PAYMENT_STATUSES.find((s) => s.value === value) || { value, label: value || '—', cls: PAYMENT_STATUSES[0].cls };
export const paymentMethodLabel = (value) => PAYMENT_METHODS.find((m) => m.value === value)?.label || value || '—';

const Badge = ({ cls, children }) => (
  <span className={`inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>
);

export const StatusBadge = ({ status }) => {
  const meta = statusMeta(status);
  return <Badge cls={meta.cls}>{meta.label}</Badge>;
};

export const PaymentBadge = ({ status }) => {
  const meta = paymentMeta(status);
  return <Badge cls={meta.cls}>{meta.label}</Badge>;
};

export const inputCls =
  'w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary ' +
  'placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30';

export const fmtMoney = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₸`);

export const fmtDateTime = (iso) =>
  iso
    ? parseServerDate(iso).toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

export const ago = (iso) => {
  if (!iso) return null;
  const m = Math.floor((Date.now() - parseServerDate(iso).getTime()) / 60000);
  if (m < 5) return 'только что';
  if (m < 60) return `${m} мин назад`;
  if (m < 1440) return `${Math.floor(m / 60)} ч назад`;
  if (m < 43200) return `${Math.floor(m / 1440)} дн назад`;
  return parseServerDate(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const SkeletonRow = () => (
  <li className="flex animate-pulse items-center gap-3 px-4 py-3">
    <div className="h-10 w-10 rounded-lg bg-bg-tertiary" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-1/3 rounded bg-bg-tertiary" />
      <div className="h-3 w-1/2 rounded bg-bg-tertiary" />
    </div>
    <div className="h-3 w-16 rounded bg-bg-tertiary" />
  </li>
);

export const Pager = ({ page, totalPages, total, shown, pageSize, loading, onPage }) => {
  if (!shown && page === 1) return null;
  const start = (page - 1) * pageSize + 1;
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border-primary px-4 py-3">
      <span className="text-xs text-text-secondary">
        {shown ? `${start}–${start + shown - 1}` : '0'}
        {typeof total === 'number' && ` из ${total.toLocaleString('ru-RU')}`}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page === 1 || loading}
          aria-label="Предыдущая страница"
          className="rounded-lg border border-border-primary px-2.5 py-1 text-sm text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
        >
          ‹
        </button>
        <span className="min-w-[4.5rem] text-center text-sm font-medium text-text-primary">
          {page} / {totalPages || 1}
        </span>
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page >= (totalPages || 1) || loading}
          aria-label="Следующая страница"
          className="rounded-lg border border-border-primary px-2.5 py-1 text-sm text-text-secondary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-40"
        >
          ›
        </button>
      </div>
    </div>
  );
};

export const Toast = ({ toast, onClose }) =>
  toast ? (
    <div
      role={toast.type === 'error' ? 'alert' : 'status'}
      className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
        toast.type === 'error' ? 'border-rose-500/30 bg-rose-500/10 text-rose-500' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
      }`}
    >
      <span>{toast.text}</span>
      <button type="button" onClick={onClose} aria-label="Закрыть" className="shrink-0 opacity-70 hover:opacity-100">
        ×
      </button>
    </div>
  ) : null;

/** Покупатель / продавец в карточке: имя, контакты и переход в профиль (там же — тариф). */
export const PersonCard = ({ title, person, onOpenUser }) => (
  <div className="rounded-lg border border-border-primary p-3">
    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">{title}</div>
    {person ? (
      <>
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-text-primary">{person.name || 'Без имени'}</span>
          <span className="font-mono text-xs text-text-muted">#{person.id}</span>
        </div>
        <div className="mt-0.5 space-y-0.5 text-xs text-text-secondary">
          {person.phone_number && <div>{person.phone_number}</div>}
          {person.email && <div className="truncate">{person.email}</div>}
        </div>
        {onOpenUser && (
          <button
            type="button"
            onClick={() => onOpenUser(person.id)}
            className="mt-2 text-xs font-medium text-[#7C3AED] hover:underline dark:text-indigo-300"
          >
            Профиль и тариф →
          </button>
        )}
      </>
    ) : (
      <div className="text-sm text-text-muted">—</div>
    )}
  </div>
);
